import { Check } from "lucide-react";
import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { settingsService } from "../services/settingsService";
import { AccountCountrySelect } from "./AccountCountrySelect";
import { SettingsRow } from "./SettingsRow";
import { SettingsSection } from "./SettingsSection";

interface LanguageOption {
  id: "es" | "en" | "pt";
  label: string;
}

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { id: "es", label: "Español" },
  { id: "en", label: "English" },
  { id: "pt", label: "Português" },
];

const TIMEZONE_OPTIONS = [
  { value: "America/Bogota", label: "(GMT-5) Bogotá · America/Bogota" },
  { value: "America/Mexico_City", label: "(GMT-6) CDMX · America/Mexico_City" },
  {
    value: "America/Sao_Paulo",
    label: "(GMT-3) São Paulo · America/Sao_Paulo",
  },
  { value: "America/Lima", label: "(GMT-5) Lima · America/Lima" },
  { value: "America/Santiago", label: "(GMT-4) Santiago · America/Santiago" },
  {
    value: "America/Buenos_Aires",
    label: "(GMT-3) Buenos Aires · America/Buenos_Aires",
  },
  { value: "America/New_York", label: "(GMT-5) New York · America/New_York" },
  {
    value: "America/Los_Angeles",
    label: "(GMT-8) Los Angeles · America/Los_Angeles",
  },
  { value: "Europe/Madrid", label: "(GMT+1) Madrid · Europe/Madrid" },
  { value: "UTC", label: "(GMT+0) UTC" },
];

export function LanguageSection() {
  const { t, activeLanguage, setLanguage } = useTranslations("settings");
  const { user } = useAuth();

  const [tz, setTz] = useState<string>(user?.timezone ?? "America/Bogota");
  const [isSavingTz, setIsSavingTz] = useState(false);
  const [country, setCountry] = useState<string>((user?.country ?? "").toUpperCase());
  const [isSavingCountry, setIsSavingCountry] = useState(false);

  useEffect(() => {
    if (user?.timezone && user.timezone !== tz) setTz(user.timezone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.timezone]);

  useEffect(() => {
    const next = (user?.country ?? "").toUpperCase();
    if (next && next !== country) setCountry(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.country]);

  const handleTzChange = async (next: string) => {
    if (!next || next === tz || isSavingTz) return;
    const previous = tz;
    setTz(next);
    setIsSavingTz(true);
    try {
      await settingsService.updateProfile({ timezone: next });
      colocaToast.success(t("language_timezone_save_success"));
    } catch (error) {
      setTz(previous);
      colocaToast.error(HttpApiService.getErrorMessage(error));
    } finally {
      setIsSavingTz(false);
    }
  };

  const handleCountryChange = async (next: string) => {
    if (!next || next === country || isSavingCountry) return;
    const previous = country;
    setCountry(next);
    setIsSavingCountry(true);
    try {
      await settingsService.updateProfile({ country: next });
      colocaToast.success(t("language_country_save_success"));
    } catch (error) {
      setCountry(previous);
      colocaToast.error(HttpApiService.getErrorMessage(error));
    } finally {
      setIsSavingCountry(false);
    }
  };

  return (
    <SettingsSection
      id="idioma"
      eyebrow={t("language_eyebrow")}
      title={t("language_section_title")}
      sub={t("language_section_description")}
    >
      <SettingsRow label={t("language_interface_label")} hint={t("language_interface_hint")}>
        <div className="grid w-full max-w-[520px] grid-cols-1 gap-2.5 sm:grid-cols-3">
          {LANGUAGE_OPTIONS.map((opt) => {
            const isActive = activeLanguage === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setLanguage(opt.id)}
                className={cn(
                  "relative cursor-pointer rounded-md border bg-background p-3 text-left transition-colors",
                  isActive
                    ? "border-primary bg-primary/5 shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-primary)_12%,transparent)]"
                    : "border-border hover:border-foreground/20",
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold">{opt.label}</span>
                  {isActive && (
                    <span className="ml-auto text-primary">
                      <Check size={14} />
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </SettingsRow>

      <div className="my-1.5 h-px bg-border" />

      <SettingsRow label={t("language_country_label")} hint={t("language_country_hint")}>
        <div className="max-w-[460px]">
          <AccountCountrySelect
            value={country}
            onValueChange={handleCountryChange}
            disabled={isSavingCountry}
            aria-label={t("language_country_label")}
          />
        </div>
      </SettingsRow>

      <div className="my-1.5 h-px bg-border" />

      <SettingsRow label={t("language_timezone_label")} hint={t("language_timezone_hint")}>
        <div className="max-w-[460px]">
          <ColocaSelect
            value={tz}
            onValueChange={handleTzChange}
            options={TIMEZONE_OPTIONS}
            disabled={isSavingTz}
            aria-label={t("language_timezone_label")}
          />
        </div>
      </SettingsRow>
    </SettingsSection>
  );
}
